// SplitSmart API Service
import axios from 'axios';
import { extractSoftPaywall, dispatchSoftPaywall } from '../utils/softPaywall';

// Robust API URL detection for different environments
const getApiBaseUrl = () => {
  // Check environment variables
  const envApiUrl = process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_API_URL;
  
  if (envApiUrl) {
    // Use the environment variable as-is if it starts with http/https
    if (envApiUrl.startsWith('http://') || envApiUrl.startsWith('https://')) {
      return envApiUrl;
    }
    // For relative paths like /api, use them directly
    if (envApiUrl.startsWith('/')) {
      return envApiUrl;
    }
    // Add https for production domains without protocol
    return `https://${envApiUrl}`;
  }
  
  // Fallback based on current domain
  if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
    return 'http://localhost:3001/api';
  } else {
    // Production fallback
    return 'https://soldikeeper-backend-production.up.railway.app/api';
  }
};

const API_BASE_URL = getApiBaseUrl();

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Handle response errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    // Soft paywall (402) — SplitSmart create / recurring gates
    if (error.response?.status === 402) {
      const soft = extractSoftPaywall(error);
      if (soft) {
        dispatchSoftPaywall(soft);
        error.softPaywall = soft;
      }
    }
    return Promise.reject(error);
  }
);

// ─── Group Templates ────────────────────────────────────────────
export const GROUP_TEMPLATES = {
  trip: {
    name: 'Trip',
    icon: '✈️',
    category: 'trip',
    description: 'Perfect for vacations and travel',
    suggestedCategories: ['accommodation', 'food', 'travel', 'entertainment'],
    defaultCurrency: 'USD',
    defaultSplitMethod: 'equal'
  },
  roommates: {
    name: 'Roommates',
    icon: '🏠',
    category: 'roommates',
    description: 'Shared living expenses',
    suggestedCategories: ['utilities', 'grocery', 'home'],
    defaultCurrency: 'USD',
    defaultSplitMethod: 'equal'
  },
  dinner: {
    name: 'Dinner',
    icon: '🍽️',
    category: 'dinner',
    description: 'Restaurant or meal splitting',
    suggestedCategories: ['restaurant', 'food'],
    defaultCurrency: 'USD',
    defaultSplitMethod: 'equal'
  },
  event: {
    name: 'Event',
    icon: '🎉',
    category: 'event',
    description: 'Parties, concerts, activities',
    suggestedCategories: ['entertainment', 'food', 'travel'],
    defaultCurrency: 'USD',
    defaultSplitMethod: 'equal'
  },
  work: {
    name: 'Work Team',
    icon: '💼',
    category: 'work',
    description: 'Office and team expenses',
    suggestedCategories: ['food', 'travel', 'other'],
    defaultCurrency: 'USD',
    defaultSplitMethod: 'equal'
  }
};

// ─── Common Currencies ──────────────────────────────────────────
export const COMMON_CURRENCIES = [
  { code: 'USD', symbol: '$', name: 'US Dollar' },
  { code: 'EUR', symbol: '€', name: 'Euro' },
  { code: 'GBP', symbol: '£', name: 'British Pound' },
  { code: 'CHF', symbol: 'Fr', name: 'Swiss Franc' },
  { code: 'CAD', symbol: 'C$', name: 'Canadian Dollar' },
  { code: 'AUD', symbol: 'A$', name: 'Australian Dollar' },
  { code: 'JPY', symbol: '¥', name: 'Japanese Yen' },
  { code: 'SEK', symbol: 'kr', name: 'Swedish Krona' },
  { code: 'NOK', symbol: 'kr', name: 'Norwegian Krone' },
  { code: 'DKK', symbol: 'kr', name: 'Danish Krone' },
  { code: 'PLN', symbol: 'zł', name: 'Polish Zloty' },
  { code: 'CZK', symbol: 'Kč', name: 'Czech Koruna' },
  { code: 'INR', symbol: '₹', name: 'Indian Rupee' },
  { code: 'BRL', symbol: 'R$', name: 'Brazilian Real' },
  { code: 'ZAR', symbol: 'R', name: 'South African Rand' },
  { code: 'MXN', symbol: 'Mex$', name: 'Mexican Peso' },
  { code: 'SGD', symbol: 'S$', name: 'Singapore Dollar' },
  { code: 'NZD', symbol: 'NZ$', name: 'New Zealand Dollar' },
  { code: 'THB', symbol: '฿', name: 'Thai Baht' },
  { code: 'KRW', symbol: '₩', name: 'South Korean Won' },
];

export class SplitSmartAPI {
  // ─── Expense CRUD ───────────────────────────────────────────────

  // Create a new expense/bill
  static async createExpense(expenseData) {
    try {
      const token = localStorage.getItem('token');

      // Require authentication for proper database persistence
      if (!token) {
        throw new Error('Authentication required. Please log in to save bills to the database.');
      }

      // Validate and sanitize the data before sending
      const sanitizedData = {
        ...expenseData,
        totalAmount: parseFloat(expenseData.totalAmount) || 0,
        baseTotal: parseFloat(expenseData.baseTotal || expenseData.totalAmount) || 0,
        participants: expenseData.participants?.map(p => ({
          ...p,
          name: p.name || p.email || 'Unknown',
          share: parseFloat(p.share) || 0,
          paidAmount: parseFloat(p.paidAmount) || 0,
          balance: parseFloat(p.balance) || 0,
          owes: parseFloat(p.owes) || 0,
          owed: parseFloat(p.owed) || 0
        })) || [],
        isFromSplitSmart: true,
        metadata: {
          source: 'SplitSmart',
          version: '2.0',
          createdAt: new Date().toISOString()
        }
      };

      const response = await api.post('/split-bills', sanitizedData);
      return { success: true, data: response.data };
    } catch (error) {
      const soft = extractSoftPaywall(error);
      if (soft) dispatchSoftPaywall(soft);
      return { 
        success: false, 
        error: error.response?.data?.message || error.message || 'Failed to save bill to database',
        softPaywall: soft || error.softPaywall || null,
        status: error.response?.status,
      };
    }
  }

  // Get all SplitSmart expenses
  static async getExpenses() {
    try {
      const token = localStorage.getItem('token');

      // Require authentication for database access
      if (!token) {
        throw new Error('Authentication required. Please log in to access your bills.');
      }

      const response = await api.get('/split-bills');
      return { success: true, data: response.data };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.message || error.message || 'Failed to load bills from database'
      };
    }
  }

  // Get specific expense by ID
  static async getExpense(id) {
    try {
      const response = await api.get(`/split-bills/${id}`);
      return { success: true, data: response.data };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.message || error.message 
      };
    }
  }

  // Update expense
  static async updateExpense(id, updateData) {
    try {
      const response = await api.put(`/split-bills/splitsmart/${id}`, {
        ...updateData,
        updatedAt: new Date().toISOString()
      });
      return { success: true, data: response.data };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.message || error.message 
      };
    }
  }

  // Delete entire split bill
  static async deleteSplitBill(id) {
    try {
      const response = await api.delete(`/split-bills/${id}`);
      return { success: true, data: response.data };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.message || error.message 
      };
    }
  }

  // ─── Split Calculation ──────────────────────────────────────────

  // Calculate expense totals
  static async calculateTotals(expenses) {
    try {
      const response = await api.post('/split-bills/calculate-total', { expenses });
      return { success: true, data: response.data };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.message || error.message 
      };
    }
  }

  // Debt simplification
  static async getSimplifiedDebts(billId) {
    try {
      const response = await api.post('/split-bills/simplify-debts', { billId });
      return { success: true, data: response.data };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.message || error.message 
      };
    }
  }

  // Get simplified debts across ALL bills for the current user.
  // Server-side single source of truth — replaces client-side generateSettlementsFromExpenses.
  static async getMyDebts() {
    try {
      const response = await api.get('/split-bills/debts/me');
      return { success: true, data: response.data?.data || response.data };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || error.message
      };
    }
  }

  // ─── Dashboard & Insights ──────────────────────────────────────

  // Get dashboard statistics
  static async getDashboardStats() {
    try {
      const token = localStorage.getItem('token');

      // Require authentication for dashboard stats
      if (!token) {
        throw new Error('Authentication required. Please log in to access dashboard statistics.');
      }

      const response = await api.get('/split-bills');
      if (response.data.success) {
        const bills = response.data.bills;

        // Calculate statistics
        const totalOwed = bills.reduce((sum, bill) => {
          return sum + (bill.userContext?.userBalance > 0 ? bill.userContext.userBalance : 0);
        }, 0);

        const totalOwing = bills.reduce((sum, bill) => {
          return sum + (bill.userContext?.userBalance < 0 ? Math.abs(bill.userContext.userBalance) : 0);
        }, 0);

        const activeGroups = bills.filter(bill => bill.status !== 'settled').length;
        const netBalance = totalOwed - totalOwing;

        return {
          success: true,
          data: {
            totalOwed: totalOwed.toFixed(2),
            totalOwing: totalOwing.toFixed(2),
            activeGroups,
            netBalance: netBalance.toFixed(2),
            recentExpenses: bills.slice(0, 5),
            totalExpenses: bills.length
          }
        };
      }
      return { success: false, error: 'Failed to fetch dashboard data' };
    } catch (error) {
      // If it's an auth error, return empty stats
      if (error.response?.status === 401) {
        return {
          success: true,
          data: {
            totalOwed: '0.00',
            totalOwing: '0.00',
            activeGroups: 0,
            netBalance: '0.00',
            recentExpenses: [],
            totalExpenses: 0
          }
        };
      }

      return { 
        success: false, 
        error: error.response?.data?.message || error.message 
      };
    }
  }

  // Get spending insights
  static async getSpendingInsights() {
    try {
      const response = await api.get('/split-bills/insights');
      return { success: true, data: response.data };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.message || error.message 
      };
    }
  }

  // ─── Group Management ──────────────────────────────────────────

  // Get groups
  static async getGroups() {
    try {
      const response = await api.get('/split-bills/groups');
      return { success: true, data: response.data };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.message || error.message 
      };
    }
  }

  // Create group
  static async createGroup(groupData) {
    try {
      const response = await api.post('/split-bills/groups', groupData);
      return { success: true, data: response.data };
    } catch (error) {
      const soft = error.softPaywall || null;
      return { 
        success: false, 
        error: error.response?.data?.message || error.message,
        softPaywall: soft,
        status: error.response?.status,
      };
    }
  }

  // Update group
  static async updateGroup(groupId, updateData) {
    try {
      const response = await api.put(`/split-bills/groups/${groupId}`, updateData);
      return { success: true, data: response.data };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.message || error.message 
      };
    }
  }

  // Delete group
  static async deleteGroup(groupId) {
    try {
      const response = await api.delete(`/split-bills/groups/${groupId}`);
      return { success: true, data: response.data };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.message || error.message 
      };
    }
  }

  // Add member to group
  static async addGroupMember(groupId, memberData) {
    try {
      const response = await api.post(`/split-bills/groups/${groupId}/members`, memberData);
      return { success: true, data: response.data };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.message || error.message 
      };
    }
  }

  // Remove member from group
  static async removeGroupMember(groupId, memberId) {
    try {
      const response = await api.delete(`/split-bills/groups/${groupId}/members/${memberId}`);
      return { success: true, data: response.data };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.message || error.message 
      };
    }
  }

  // ─── Group Sharing (Public) ────────────────────────────────────

  // Get group by share code (public, no auth needed)
  static async getGroupByShareCode(shareCode) {
    try {
      const response = await api.get(`/split-bills/groups/join/${shareCode}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching group by share code:', error);
      throw error;
    }
  }

  // Join group by share code (public, no auth needed)
  static async joinGroupByShareCode(shareCode, { name, email }) {
    try {
      const response = await api.post(`/split-bills/groups/join/${shareCode}`, { name, email });
      return response.data;
    } catch (error) {
      console.error('Error joining group:', error);
      throw error;
    }
  }

  // ─── Settlements ───────────────────────────────────────────────

  // Process settlement
  static async processSettlement(billId, settlementData) {
    try {
      const response = await api.post(`/splitsmart/${billId}/settle`, {
        settlementData
      });
      return { success: true, data: response.data };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.message || error.message 
      };
    }
  }

  static async confirmSettlement(billId, settlementId) {
    try {
      const response = await api.put(`/split-bills/${billId}/settlements/${settlementId}/confirm`);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.message || error.message };
    }
  }

  static async disputeSettlement(billId, settlementId, reason) {
    try {
      const response = await api.put(`/split-bills/${billId}/settlements/${settlementId}/dispute`, { reason });
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.message || error.message };
    }
  }

  // Get settlements from backend
  static async getSettlements() {
    try {
      const token = localStorage.getItem('token');

      if (!token) {
        throw new Error('Authentication required. Please log in to access settlements.');
      }

      const response = await api.get('/split-bills/settlements');

      // Extract settlements array from nested response structure
      // response.data = { success: true, data: { settlements: [...] } }
      const settlements = response.data?.data?.settlements || response.data?.settlements || [];

      return { 
        success: true, 
        data: settlements
      };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.message || error.message 
      };
    }
  }

  // Update settlement status in database with traceability
  static async updateSettlement(settlementId, updateData) {
    try {
      const token = localStorage.getItem('token');

      if (!token) {
        throw new Error('Authentication required. Please log in to update settlements.');
      }

      // Add traceability data
      const settlementUpdateData = {
        ...updateData,
        updatedAt: new Date().toISOString(),
        updatedBy: 'user',
        source: 'SplitSmart Dashboard',
        traceability: {
          action: updateData.status === 'completed' ? 'payment_completed' : 'settlement_updated',
          timestamp: new Date().toISOString(),
          userAgent: navigator.userAgent,
          platform: 'web'
        }
      };

      const response = await api.put(`/settlements/${settlementId}`, settlementUpdateData);
      return { 
        success: true, 
        data: response.data 
      };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.message || error.message 
      };
    }
  }

  // Create a new settlement transaction with full traceability
  static async createSettlementTransaction(settlementData) {
    try {
      const token = localStorage.getItem('token');

      if (!token) {
        throw new Error('Authentication required. Please log in to create settlement transactions.');
      }

      // Enhanced settlement data with traceability
      const enhancedSettlementData = {
        ...settlementData,
        createdAt: new Date().toISOString(),
        source: 'SplitSmart Dashboard',
        status: 'completed',
        metadata: {
          paymentMethod: settlementData.paymentMethod || 'unknown',
          deviceInfo: {
            userAgent: navigator.userAgent,
            platform: 'web',
            timestamp: new Date().toISOString()
          },
          transactionId: `SST-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          version: '2.0'
        },
        traceability: {
          action: 'settlement_created',
          timestamp: new Date().toISOString(),
          source: 'SplitSmart',
          method: 'api_call',
          userInitiated: true
        }
      };

      const response = await api.post('/split-bills/settlements', enhancedSettlementData);
      return { 
        success: true, 
        data: response.data 
      };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.message || error.message,
        details: {
          status: error.response?.status,
          statusText: error.response?.statusText,
          data: error.response?.data
        }
      };
    }
  }

  // ─── Payments & Reminders ──────────────────────────────────────

  // Send payment reminder
  static async sendReminder(billId, participantId) {
    try {
      const response = await api.post(`/split-bills/${billId}/send-reminder`, {
        participantId
      });
      return { success: true, data: response.data };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.message || error.message 
      };
    }
  }

  // Mark payment as complete
  static async markPaymentComplete(billId, participantId) {
    try {
      const response = await api.post(`/split-bills/${billId}/mark-paid`, {
        participantId
      });
      return { success: true, data: response.data };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.message || error.message 
      };
    }
  }

  // ─── Receipts & Media ──────────────────────────────────────────

  // Upload receipt image
  static async uploadReceipt(billId, file) {
    try {
      const formData = new FormData();
      formData.append('receipt', file);

      const response = await api.post(`/split-bills/${billId}/receipt`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      return { success: true, data: response.data };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.message || error.message 
      };
    }
  }

  // ─── Export ─────────────────────────────────────────────────────

  // CSV export — returns blob for download
  static async exportBill(billId, format = 'csv') {
    try {
      const response = await api.get(`/split-bills/${billId}/export?format=${format}`, {
        responseType: 'blob'
      });
      return { success: true, data: response.data };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.message || error.message 
      };
    }
  }

  // ─── Comments ───────────────────────────────────────────────────

  // Add comment to an expense
  static async addExpenseComment(billId, expenseIndex, text) {
    try {
      const response = await api.post(`/split-bills/${billId}/expenses/${expenseIndex}/comments`, { text });
      return { success: true, data: response.data };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.message || error.message 
      };
    }
  }

  // Delete comment from an expense
  static async deleteExpenseComment(billId, expenseIndex, commentId) {
    try {
      const response = await api.delete(`/split-bills/${billId}/expenses/${expenseIndex}/comments/${commentId}`);
      return { success: true, data: response.data };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.message || error.message 
      };
    }
  }

  // ─── Recurring Bills ────────────────────────────────────────────

  // Create a recurring bill
  static async createRecurringBill(billData) {
    try {
      const response = await api.post('/split-bills/recurring', billData);
      return { success: true, data: response.data };
    } catch (error) {
      const soft = error.softPaywall || null;
      return {
        success: false,
        error: error.response?.data?.message || error.message,
        softPaywall: soft,
        status: error.response?.status,
      };
    }
  }

  // Get all recurring bills
  static async getRecurringBills() {
    try {
      const response = await api.get('/split-bills/recurring');
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.message || error.message };
    }
  }

  // Update recurring bill configuration
  static async updateRecurringConfig(billId, config) {
    try {
      const response = await api.put(`/split-bills/recurring/${billId}`, config);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.message || error.message };
    }
  }

  // Delete a recurring bill
  static async deleteRecurringBill(billId) {
    try {
      const response = await api.delete(`/split-bills/recurring/${billId}`);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.message || error.message };
    }
  }

  // ─── Multi-Currency ─────────────────────────────────────────────

  // Get available currencies
  static async getCurrencies() {
    try {
      const response = await api.get('/split-bills/currencies');
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.message || error.message };
    }
  }

  // Convert amount between currencies
  static async convertCurrency(amount, from, to) {
    try {
      const response = await api.post('/split-bills/convert', { amount, from, to });
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.message || error.message };
    }
  }

  // Get exchange rates for a base currency
  static async getExchangeRates(base = 'USD') {
    try {
      const response = await api.get(`/split-bills/rates?base=${base}`);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.message || error.message };
    }
  }

  // ─── Payment Methods & Links ────────────────────────────────────

  // Get saved payment methods
  static async getPaymentMethods() {
    try {
      const response = await api.get('/split-bills/payment-methods');
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.message || error.message };
    }
  }

  // Add a new payment method
  static async addPaymentMethod(methodData) {
    try {
      const response = await api.post('/split-bills/payment-methods', methodData);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.message || error.message };
    }
  }

  // Remove a payment method
  static async removePaymentMethod(methodId) {
    try {
      const response = await api.delete(`/split-bills/payment-methods/${methodId}`);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.message || error.message };
    }
  }

  // Generate a shareable payment link
  static async generatePaymentLink(data) {
    try {
      const response = await api.post('/split-bills/payment-link', data);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.message || error.message };
    }
  }

  // ─── Member Expenses ────────────────────────────────────────────

  // Add an expense to an existing bill
  static async addExpenseToBill(billId, expenseData) {
    try {
      const response = await api.post(`/split-bills/${billId}/add-expense`, expenseData);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.message || error.message };
    }
  }

  // ── Guest Access (public, no auth needed for these) ──

  static async getBillByShareCode(shareCode) {
    try {
      const response = await axios.get(`${API_BASE_URL}/split-bills/join/${shareCode}`);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.message || error.message };
    }
  }

  static async joinBillAsGuest(shareCode, name, guestId) {
    try {
      const response = await axios.post(`${API_BASE_URL}/split-bills/join/${shareCode}`, { name, guestId });
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.message || error.message };
    }
  }

  static async addGuestExpense(shareCode, expenseData) {
    try {
      const response = await axios.post(`${API_BASE_URL}/split-bills/join/${shareCode}/expense`, expenseData);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.message || error.message };
    }
  }

  // ── Link Bill to Account (auth user links a shared bill to their account) ──
  static async linkBillToAccount(shareCode) {
    try {
      const response = await api.post(`/split-bills/link/${shareCode}`);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.message || error.message };
    }
  }

  // ── Expense Edit/Delete (auth user) ──
  static async updateExpense(billId, expenseIndex, data) {
    try {
      const response = await api.put(`/split-bills/${billId}/expense/${expenseIndex}`, data);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.message || error.message };
    }
  }

  static async deleteExpense(billId, expenseIndex) {
    try {
      const response = await api.delete(`/split-bills/${billId}/expense/${expenseIndex}`);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.message || error.message };
    }
  }

  // ── Expense Edit/Delete (guest) ──
  static async updateGuestExpense(shareCode, expenseIndex, data) {
    try {
      const response = await axios.put(`${API_BASE_URL}/split-bills/join/${shareCode}/expense/${expenseIndex}`, data);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.message || error.message };
    }
  }

  static async deleteGuestExpense(shareCode, expenseIndex, guestId) {
    try {
      const response = await axios.delete(`${API_BASE_URL}/split-bills/join/${shareCode}/expense/${expenseIndex}`, { data: { guestId } });
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.message || error.message };
    }
  }

  // ── Edit Lock Toggle (auth, creator only) ──
  static async toggleEditLock(billId) {
    try {
      const response = await api.put(`/split-bills/${billId}/edit-lock`);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.message || error.message };
    }
  }

  // ── Update Bill Settings (auth, creator only) ──
  static async updateSplitSmartBill(billId, updateData) {
    try {
      const response = await api.put(`/split-bills/splitsmart/${billId}`, {
        ...updateData,
        updatedAt: new Date().toISOString()
      });
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.message || error.message };
    }
  }

  // ── Name Editing ──
  static async updateGuestName(shareCode, guestId, newName) {
    try {
      const response = await axios.put(`${API_BASE_URL}/split-bills/join/${shareCode}/name`, { guestId, newName });
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.message || error.message };
    }
  }

  static async updateParticipantName(billId, participantIndex, newName) {
    try {
      const response = await api.put(`/split-bills/${billId}/participant-name`, { participantIndex, newName });
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.message || error.message };
    }
  }

  static async addParticipant(billId, data) {
    try {
      const response = await api.post(`/split-bills/${billId}/add-participant`, data);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.message || error.message };
    }
  }

  static async generateShareLink(billId) {
    try {
      const response = await api.post(`/split-bills/${billId}/share`);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.message || error.message };
    }
  }
}

export default SplitSmartAPI;
